import React, { useState, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { Link } from 'react-router-dom';
import { formatNYDateTime, formatNYDate, formatNYTime, nyDateKey } from '../utils/datetime';
import './CalendarPage.css';

const CalendarPage = () => {
  const { getAllCases } = useAppContext();
  const [allCases, setAllCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterRange, setFilterRange] = useState('week');
  const [filteredCases, setFilteredCases] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const data = await getAllCases();
      setAllCases(data);
      setLoading(false);
    };
    load();
  }, [getAllCases]);

  useEffect(() => {
    let start = new Date();
    let end = new Date();
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    if (selectedDay) {
      start = new Date(selectedDay);
      start.setHours(0, 0, 0, 0);
      end = new Date(selectedDay);
      end.setHours(23, 59, 59, 999);
    } else {
      switch (filterRange) {
        case 'today': break;
        case 'tomorrow':
          start.setDate(start.getDate() + 1);
          end.setDate(end.getDate() + 1);
          break;
        case 'week':
          end.setDate(end.getDate() + 7);
          break;
        case 'month':
          end.setMonth(end.getMonth() + 1);
          break;
        case 'range':
          if (dateRange.start && dateRange.end) {
            start = new Date(dateRange.start);
            end = new Date(dateRange.end);
            end.setHours(23, 59, 59, 999);
          }
          break;
      }
    }

    const filtered = allCases
      .filter((c) => {
        if (!c.courtDateTime) return false;
        const dt = new Date(c.courtDateTime);
        return dt >= start && dt <= end;
      })
      .sort((a, b) => new Date(a.courtDateTime) - new Date(b.courtDateTime));

    setFilteredCases(filtered);
  }, [filterRange, selectedDay, dateRange, allCases]);

  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = new Date(year, month, 1).getDay();

  // Key for a calendar grid cell (a local Date built from y/m/d), kept tz-neutral.
  const getDateKey = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
      d.getDate(),
    ).padStart(2, '0')}`;

  const casesByDate = allCases.reduce((acc, c) => {
    if (!c.courtDateTime) return acc;
    const key = nyDateKey(c.courtDateTime); // group by New York calendar day
    if (!key) return acc;
    if (!acc[key]) acc[key] = [];
    acc[key].push(c);
    return acc;
  }, {});

  const handleDayClick = (day) => {
    const date = new Date(year, month, day);
    setSelectedDay(date);
    setFilterRange('custom-day');
  };

  const handleMonthNav = (dir) => {
    const next = new Date(calendarDate);
    next.setMonth(next.getMonth() + dir);
    setCalendarDate(next);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('en-US');
  };

  const getFilterLabel = () => {
    if (selectedDay) {
      return `Cases for ${formatDate(selectedDay)}`;
    }

    switch (filterRange) {
      case 'today': return 'Cases Today';
      case 'tomorrow': return 'Cases Tomorrow';
      case 'week': return 'Cases This Week';
      case 'month': return 'Cases This Month';
      case 'range':
        return dateRange.start && dateRange.end
          ? `Cases ${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`
          : 'Cases (Date Range)';
      default: return '';
    }
  };

  const getPrintTimestamp = () => {
    const now = new Date();
    const date = now.toLocaleDateString('en-US');
    const time = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return `PRINTED ON ${date} at ${time}`;
  };

  return (
    <div className="container calendar-page">
      <h2 className="no-print">Court Appearance Calendar</h2>

      {loading ? (
        <div className="loading-spinner">Loading calendar...</div>
      ) : (
        <>
          <div className="calendar-controls no-print">
            <select value={filterRange} onChange={(e) => { setFilterRange(e.target.value); setSelectedDay(null); }}>
              <option value="today">Cases Today</option>
              <option value="tomorrow">Cases Tomorrow</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="range">Date Range</option>
            </select>

            {filterRange === 'range' && (
              <>
                <input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} />
                <input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} />
              </>
            )}

            <button onClick={() => window.print()}>Print Schedule</button>
          </div>

          <div className="calendar-header no-print">
            <button onClick={() => handleMonthNav(-1)}>&lt;</button>
            <h3>{calendarDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
            <button onClick={() => handleMonthNav(1)}>&gt;</button>
          </div>

          <div className="calendar-weekdays no-print">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
              <div key={d} className="weekday">{d}</div>
            ))}
          </div>

          <div className="calendar-grid no-print">
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`blank-${i}`} className="calendar-day blank" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const date = new Date(year, month, day);
              const key = getDateKey(date);
              const hasCase = casesByDate[key];
              const isSelected = selectedDay && getDateKey(new Date(selectedDay)) === key;

              return (
                <div
                  key={i}
                  className={`calendar-day ${hasCase ? 'has-case' : ''} ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleDayClick(day)}
                >
                  {day}
                </div>
              );
            })}
          </div>

          <table className="calendar-case-table no-print">
            <thead>
              <tr>
                <th>LastName / FirstName</th>
                <th>Court Date / Time</th>
                <th>County</th>
                <th>Ticket #</th>
                <th>Phone</th>
                <th>Phone #2</th>
                <th>Comments</th>
                <th>Disposition</th>
              </tr>
            </thead>
            <tbody>
              {filteredCases.map((c, i) => (
                <tr key={i}>
                  <td><Link to={`/case/${c.id}`} className="case-link">{c.name || `${c.lastName} ${c.firstName}`}</Link></td>
                  <td>{formatNYDateTime(c.courtDateTime)}</td>
                  <td>{c.incidentCounty}</td>
                  <td>{c.appearanceNumber}</td>
                  <td>{c.phone}</td>
                  <td>{c.secondaryPhone}</td>
                  <td>{c.comments}</td>
                  <td>{c.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* 🖨️ Print-only view */}
          <div className="print-only">
            <div className="print-header">
              <div className="print-timestamp">{getPrintTimestamp()}</div>
              <h3>Calendar - {getFilterLabel()}</h3>
            </div>
            <table className="calendar-case-table">
              <thead>
                <tr>
                  <th>Court Date</th>
                  <th>Court Time</th>
                  <th>County</th>
                  <th>LastName / FirstName</th>
                  <th>Ticket#</th>
                  <th>Phone #1</th>
                  <th>Phone #2</th>
                  <th>Officer Name</th>
                  <th>Comments</th>
                  <th>Disposition</th>
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((c, i) => {
                  return (
                    <tr key={i}>
                      <td>{formatNYDate(c.courtDateTime)}</td>
                      <td>{formatNYTime(c.courtDateTime)}</td>
                      <td>{c.incidentCounty}</td>
                      <td>{c.name || `${c.lastName} ${c.firstName}`}</td>
                      <td>{c.appearanceNumber}</td>
                      <td>{c.phone}</td>
                      <td>{c.secondaryPhone}</td>
                      <td>{c.officerName}</td>
                      <td>{c.comments}</td>
                      <td>{c.status}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
      <footer className="settings-footer">
        © {new Date().getFullYear()} Zbrella Inc. All rights reserved.
      </footer>
    </div>
  );
};

export default CalendarPage;
